import { keepPreviousData, type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Assets } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { toWorkspaceAgentPillCoversByAssetId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPillCoversByAssetId';

export const PRIVATE_WORKSPACE_AGENT_PILL_ASSET_COVERS_KEY = 'PRIVATE_WORKSPACE_AGENT_PILL_ASSET_COVERS';

export type PrivateWorkspaceAgentPillAssetCoversParams = Omit<
  NonNullable<Parameters<typeof Assets.listCoverVersionsByAssetIds>[0]>,
  'assetIds' | 'withBoards' | 'workspaceId'
> & {
  assetIds: readonly string[];
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentPillAssetCoversKey = [
  typeof PRIVATE_WORKSPACE_AGENT_PILL_ASSET_COVERS_KEY,
  PrivateWorkspaceAgentPillAssetCoversParams,
];

export const getPrivateWorkspaceAgentPillAssetCoversKey = (
  params: PrivateWorkspaceAgentPillAssetCoversParams,
): PrivateWorkspaceAgentPillAssetCoversKey => [PRIVATE_WORKSPACE_AGENT_PILL_ASSET_COVERS_KEY, params];

export const isPrivateWorkspaceAgentPillAssetCoversKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentPillAssetCoversKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_PILL_ASSET_COVERS_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentPillAssetCoversOptions = {
  enabled?: boolean;
};

/**
 * Resolves `@[asset:<assetId>]` monikers in one request. The moniker names the asset, so the cover
 * version is what the chip can draw and open.
 */
export const usePrivateWorkspaceAgentPillAssetCovers = (
  params: PrivateWorkspaceAgentPillAssetCoversParams,
  options?: UsePrivateWorkspaceAgentPillAssetCoversOptions,
) => {
  const { assetIds, workspaceId } = params;
  const stableAssetIds = [...new Set(assetIds)].sort((left, right) => left.localeCompare(right));
  const keyParams = { ...params, assetIds: stableAssetIds } as const;

  return useQuery({
    queryKey: getPrivateWorkspaceAgentPillAssetCoversKey(keyParams),
    queryFn:
      workspaceId && stableAssetIds.length > 0
        ? () =>
            callAirApi(() =>
              Assets.listCoverVersionsByAssetIds({
                assetIds: stableAssetIds,
                workspaceId,
              }),
            )
        : skipToken,
    enabled: Boolean(workspaceId) && stableAssetIds.length > 0 && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    select: toWorkspaceAgentPillCoversByAssetId,
  });
};
